import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import api from "../../services/api";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-NG", { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const DASHBOARDS = {
  accounts: {
    title: "Accounts dashboard",
    subtitle: "Monitor posting queues, ledger activity, and reconciliation workloads.",
    cards: [
      ["pending_wallet_transactions", "Pending wallet requests", formatNumber],
      ["pending_wallet_amount", "Wallet value awaiting review", formatCurrency],
      ["pending_postings", "Pending back-office postings", formatNumber],
      ["pending_posting_amount", "Posting value awaiting approval", formatCurrency],
      ["draft_postings", "Draft postings", formatNumber],
      ["posted_batches_today", "Ledger batches posted today", formatNumber],
      ["active_ledger_accounts", "Active ledger accounts", formatNumber],
    ],
    actions: [
      ["/admin/general-ledger/pending", "Review pending postings", "Approve or reject queued client transactions."],
      ["/admin/backoffice/postings", "Back-office postings", "Capture and submit manual accounting movements."],
      ["/admin/general-ledger", "General ledger", "Inspect posted journal entries and balances."],
      ["/admin/finance/bank-accounts", "Chart of accounts", "Maintain ledger and bank account mappings."],
      ["/admin/reports", "Accounts reports", "Review and export finance and ledger reports."],
    ],
  },
  compliance: {
    title: "Compliance dashboard",
    subtitle: "Track onboarding throughput, KYC decisions, and approved-client records.",
    cards: [
      ["pending_onboarding", "Pending onboarding reviews", formatNumber],
      ["recent_submissions", "Submissions in the last 7 days", formatNumber],
      ["approved_clients", "KYC-approved clients", formatNumber],
      ["rejected_onboarding", "Rejected applications", formatNumber],
      ["manual_onboarding", "Manually created applications", formatNumber],
    ],
    actions: [
      ["/admin/compliance", "Review onboarding", "Open pending applications and record decisions."],
      ["/admin/compliance/manual-entry", "Manual onboarding", "Create an application on a client's behalf."],
      ["/admin/compliance/crm", "Approved-client CRM", "Review and maintain approved client records."],
      ["/admin/compliance/staff", "Provision compliance staff", "Create another compliance team account."],
      ["/admin/finance/client-360", "Client 360", "Inspect consolidated client financial information."],
    ],
  },
  finance: {
    title: "Finance dashboard",
    subtitle: "Monitor client flows, company cash movement, and active investment exposure.",
    cards: [
      ["total_deposits", "Client deposits", formatCurrency],
      ["total_withdrawals", "Client withdrawals", formatCurrency],
      ["net_client_wallet_flow", "Net client wallet flow", formatCurrency],
      ["company_inflows", "Company inflows", formatCurrency],
      ["company_outflows", "Company outflows", formatCurrency],
      ["net_company_cashflow", "Net company cashflow", formatCurrency],
      ["active_client_investments", "Active client investments", formatNumber],
      ["active_client_principal", "Active client principal", formatCurrency],
    ],
    actions: [
      ["/admin/finance/client-360", "Client 360", "Review client wallets, investments, and activity."],
      ["/admin/investments/subscriptions", "Subscribed investments", "Review active client investments, balances, and schedules."],
      ["/admin/fixed-income", "Fixed-income wizard", "Simulate and create fixed-income schedules."],
      ["/admin/firm/investments", "Firm investments", "Originate, approve, and settle firm positions."],
      ["/admin/firm/reports", "Firm reports", "Review AUM, cashflows, and maturity exposure."],
    ],
  },
};

const DepartmentDashboard = ({ department }) => {
  const config = DASHBOARDS[department];
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.adminFetchDepartmentDashboard(department);
      setMetrics(data?.metrics || {});
    } catch (err) {
      setError(err.message || "Unable to load department analytics.");
    } finally {
      setLoading(false);
    }
  }, [department]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const cards = useMemo(
    () =>
      config.cards.map(([key, label, formatter]) => ({
        key,
        label,
        value: formatter(metrics[key]),
      })),
    [config.cards, metrics]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <section>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            {department}
          </p>
          <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-slate-900">{config.title}</h1>
              <p className="mt-1 text-sm text-slate-500">{config.subtitle}</p>
            </div>
            <button
              type="button"
              onClick={loadMetrics}
              disabled={loading}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
            >
              {loading ? "Refreshing..." : "Refresh analytics"}
            </button>
          </div>
        </section>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <article key={card.key} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className="mt-3 text-2xl font-semibold text-slate-900">
                {loading ? "—" : card.value}
              </p>
            </article>
          ))}
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Department workflows</h2>
            <p className="mt-1 text-sm text-slate-500">
              Open the functions assigned to the {department} team.
            </p>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {config.actions.map(([to, label, description]) => (
              <Link
                key={to}
                to={to}
                className="rounded-xl border border-slate-200 p-4 transition hover:border-emerald-300 hover:bg-emerald-50"
              >
                <p className="font-semibold text-slate-900">{label}</p>
                <p className="mt-1 text-sm text-slate-500">{description}</p>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DepartmentDashboard;
